"use client";

// recharts dựng trên React context + DOM nên chỉ chạy được ở client.
// Thiếu directive này thì mọi server component import barrel @chotca/ui đều nổ
// "createContext is not a function" khi Next đánh giá module trên server.
import * as React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { ChartCard } from "./chart-card";
import { CHART_TOOLTIP_STYLE, paletteColor } from "./chart-theme";

export interface DonutSlice {
  label: string;
  value: number;
  color?: string;
}

export interface DonutChartProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  data: DonutSlice[];
  height?: number;
  /** Big number shown in the donut hole. */
  centerValue?: React.ReactNode;
  /** Caption under the center value. */
  centerLabel?: React.ReactNode;
  className?: string;
}

/** Donut (pie with a hole) with an optional center stat + side legend. */
export function DonutChart({
  title,
  subtitle,
  action,
  data,
  height = 220,
  centerValue,
  centerLabel,
  className,
}: DonutChartProps) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const slices = data.map((d, i) => ({
    ...d,
    color: d.color ?? paletteColor(i),
  }));

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      action={action}
      className={className}
    >
      <div className="flex items-center gap-4">
        <div className="relative" style={{ width: height, height }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="label"
                innerRadius="62%"
                outerRadius="100%"
                paddingAngle={total > 0 ? 2 : 0}
                stroke="none"
              >
                {slices.map((s) => (
                  <Cell key={s.label} fill={s.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
          {centerValue != null ? (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-bold text-ink">{centerValue}</span>
              {centerLabel ? (
                <span className="text-[10px] uppercase tracking-wide text-ash">
                  {centerLabel}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <ul className="min-w-0 flex-1 space-y-1.5">
          {slices.map((s) => (
            <li
              key={s.label}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <span className="truncate text-ink/80">{s.label}</span>
              </span>
              <span className="shrink-0 font-semibold text-ink">{s.value}</span>
            </li>
          ))}
        </ul>
      </div>
    </ChartCard>
  );
}
